import { supabase, isConfigured } from './supabase';
import type { Contact, ContactStatus, SentChannel } from '../types';
import { updateContact } from './contacts';

const todayIsoDate = () => new Date().toISOString().slice(0, 10);

export const QUEUE_READY_STATUSES: ContactStatus[] = ['new', 'enriched', 'drafted', 'approved'];

export interface TodayQueueResult {
  leads: Contact[];
  total: number;
  ready: number;
  sent: number;
  skippedToday: number;
}

export async function getTodayQueue(
  teamId: string,
  options?: { allTeams?: boolean }
): Promise<TodayQueueResult> {
  const empty: TodayQueueResult = { leads: [], total: 0, ready: 0, sent: 0, skippedToday: 0 };
  if (!isConfigured) return empty;

  let query = supabase.from('sales_contacts').select('*').order('created_at', { ascending: false });
  if (!options?.allTeams) query = query.eq('team_id', teamId);

  const { data, error } = await query;
  if (error || !data) {
    console.error('[outbound] queue fetch failed:', error);
    return empty;
  }

  const today = todayIsoDate();
  const contacts = data as Contact[];
  const skippedToday = contacts.filter((c) => c.queue_skipped_on === today).length;
  const sent = contacts.filter((c) => c.status === 'sent' || c.status === 'replied' || c.status === 'meeting').length;
  const readyRows = contacts.filter((c) => QUEUE_READY_STATUSES.includes(c.status) && c.queue_skipped_on !== today);

  const rank = (c: Contact) => {
    const statusRank = { approved: 0, drafted: 1, enriched: 2, new: 3 }[c.status] ?? 9;
    const fitRank = { strong: 0, maybe: 1, weak: 2 }[c.icp_fit || 'maybe'] ?? 3;
    return statusRank * 10 + fitRank;
  };

  return {
    leads: readyRows.sort((a, b) => rank(a) - rank(b)),
    total: contacts.length,
    ready: readyRows.length,
    sent,
    skippedToday,
  };
}

export async function skipQueueToday(contactId: string): Promise<boolean> {
  const result = await updateContact({ id: contactId, queue_skipped_on: todayIsoDate() });
  return !!result;
}

export async function markLeadStatus(
  contactId: string,
  status: ContactStatus,
  extras?: Partial<Contact>
): Promise<boolean> {
  const result = await updateContact({ id: contactId, status, ...extras });
  return !!result;
}

export async function markLeadSent(
  contactId: string,
  channel: SentChannel
): Promise<boolean> {
  return markLeadStatus(contactId, 'sent', {
    last_touch_on: todayIsoDate(),
    sent_on: new Date().toISOString(),
    next_step: channel === 'both' ? 'Wait for reply (email + LinkedIn)' : `Wait for reply (${channel})`,
  });
}

export async function attachLeadDraft(
  contactId: string,
  draft: {
    email_subject?: string | null;
    email_body?: string | null;
    linkedin_note?: string | null;
    research_notes?: string | null;
    next_step?: string | null;
  },
  currentStatus?: ContactStatus
): Promise<boolean> {
  const keepStatus = currentStatus === 'approved' || currentStatus === 'sent' || currentStatus === 'replied';
  return markLeadStatus(contactId, keepStatus ? currentStatus : 'drafted', {
    ...draft,
    drafted_on: new Date().toISOString(),
  });
}

export interface OutboundStats {
  byStatus: Record<string, number>;
  sentThisWeek: number;
  repliedThisWeek: number;
  draftedUnsentOver3Days: number;
  bySegment: Record<string, number>;
  byCountry: Record<string, number>;
}

export async function getOutboundStats(
  teamId: string,
  options?: { allTeams?: boolean }
): Promise<OutboundStats> {
  const empty: OutboundStats = {
    byStatus: {},
    sentThisWeek: 0,
    repliedThisWeek: 0,
    draftedUnsentOver3Days: 0,
    bySegment: {},
    byCountry: {},
  };
  if (!isConfigured) return empty;

  let query = supabase
    .from('sales_contacts')
    .select('status, segment, country, drafted_on, sent_on, replied_on');
  if (!options?.allTeams) query = query.eq('team_id', teamId);

  const { data, error } = await query;
  if (error || !data) return empty;

  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const threeDaysAgo = Date.now() - 3 * 24 * 60 * 60 * 1000;
  const stats = { ...empty };

  for (const row of data as Contact[]) {
    stats.byStatus[row.status] = (stats.byStatus[row.status] || 0) + 1;
    if (row.segment) stats.bySegment[row.segment] = (stats.bySegment[row.segment] || 0) + 1;
    if (row.country) stats.byCountry[row.country] = (stats.byCountry[row.country] || 0) + 1;
    if (row.sent_on && new Date(row.sent_on).getTime() >= weekAgo) stats.sentThisWeek++;
    if (row.replied_on && new Date(row.replied_on).getTime() >= weekAgo) stats.repliedThisWeek++;
    if (
      row.status === 'drafted' &&
      row.drafted_on &&
      new Date(row.drafted_on).getTime() < threeDaysAgo
    ) {
      stats.draftedUnsentOver3Days++;
    }
  }

  return stats;
}

export function ctaHintForSegment(segment: string | null | undefined): string {
  if (segment === 'photographer' || segment === 'solo_agent') {
    return 'CTA: offer 10 free credits and 50% off the first top-up.';
  }
  if (segment === 'agency_marketing') {
    return 'CTA: seniors (CMO / VP / Head of Marketing) — no free credits. Offer a short working session.';
  }
  return 'CTA: keep it specific to their role. Do not invent a free-credit offer for seniors.';
}

export function looksFabricatedEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return /^(info|hello|contact|admin|sales|office)@/i.test(email.trim());
}
